/* eslint-disable no-param-reassign */
import * as _ from 'lodash-es';
import type { Socket } from 'phoenix';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { addBreadcrumb } from '@sentry/react';
import { purgeApolloCache } from '../app/apollo/cache';
import { updateApolloLink } from '../app/apollo/getApolloClient';
import type { AppThunk } from '../app/redux/getReduxStore';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { AllowPublicContentBy } from '../graphql/generated/graphql';
import {
  BillingSummaryStatus,
  PlanTier,
  UserType,
} from '../graphql/generated/graphql';
import { miscToasts } from '../toasts/toasts';
import { purgeUIPersistedState } from '../ui/atoms';
import { UIActions } from '../ui/uiSlice';
import Logger from '../utils/Logger';
import SlabSocket from '../utils/SlabSocket';
import { initAnalytics } from '../utils/analytics';
import LoggedInTeams from '../utils/cookie/LoggedInTeams';
import { isElectron } from '../utils/desktopClient';
import intercomFacade from '../utils/intercomFacade';
import { purgePersisted } from '../utils/persistRedux';
import authTokenUtil from './authTokenUtil';
import visitorAuthTokenUtil from './visitorAuthTokenUtil';

// eslint-disable-next-line slab-graphql/no-unused-fields
const SET_ORGANIZATION_FRAGMENT = gql(`
  fragment setCurrentOrganization_organization on Organization {
    id
    email
    host
    effectiveHost
    name
    logo {
      thumb
    }
    planTier
    periodEndAt
    trialEndAt
    isTrialEnded
    isTrialEndingSoon
    isLocked
    billingStatus
    settings {
      allowPublicContentBy
      seatLimit
    },
    isVerified
  }
`);

const initialOrganization: DocumentType<typeof SET_ORGANIZATION_FRAGMENT> = {
  id: '',
  email: '',
  host: '',
  effectiveHost: '',
  name: '',
  logo: { thumb: null },
  planTier: PlanTier.Startup,
  periodEndAt: null,
  trialEndAt: null,
  isTrialEnded: false,
  isTrialEndingSoon: false,
  isLocked: false,
  billingStatus: BillingSummaryStatus.Active,
  settings: {
    allowPublicContentBy: null,
    seatLimit: null,
  },
  isVerified: false,
};

const initialUser = {
  id: '',
  name: '',
  avatar: {
    thumb: null,
    original: null,
  },
  title: '',
  type: UserType.User,
  email: '',
  settings: {},
  description: null,
  insertedAt: '',
};

type SessionState = {
  organization: DocumentType<typeof SET_ORGANIZATION_FRAGMENT>;
  user: Omit<
    DocumentType<typeof START_SESSION_SESSION_FRAGMENT>['user'],
    '__typename'
  >;
  socket?: Socket;
};

const initialState: SessionState = {
  organization: initialOrganization,
  user: initialUser,
  socket: undefined,
};

// eslint-disable-next-line slab-graphql/no-unused-fields
const SET_USER_SETTING_USER_SETTING_FRAGMENT = gql(`
  fragment setUserSetting_userSetting on UserSettings {
    allowAccountAccess
    uiTheme
    aiAutofix
    aiAsk
    preferDesktop
  }
`);

const sessionSlice = createSlice({
  name: 'sessionSlice',
  initialState,
  reducers: {
    sessionStart(state, action: PayloadAction<{ socket: Socket }>) {
      state.socket = action.payload.socket;
    },
    visitorSessionStart(state, action: PayloadAction<{ socket: Socket }>) {
      state.socket = action.payload.socket;
    },
    setCurrentOrganization(
      state,
      action: PayloadAction<
        Partial<DocumentType<typeof SET_ORGANIZATION_FRAGMENT>>
      >,
    ) {
      state.organization = { ...state.organization, ...action.payload };
    },
    setAllowPublicContentBy(
      state,
      action: PayloadAction<AllowPublicContentBy | null>,
    ) {
      state.organization = {
        ...state.organization,
        settings: {
          ...state.organization.settings,
          allowPublicContentBy: action.payload,
        },
      };
    },
    setCurrentUser(
      state,
      action: PayloadAction<
        DocumentType<typeof START_SESSION_SESSION_FRAGMENT>['user']
      >,
    ) {
      state.user = { ...state.user, ...action.payload };
    },
    setUserSetting(
      state,
      action: PayloadAction<
        DocumentType<typeof SET_USER_SETTING_USER_SETTING_FRAGMENT>
      >,
    ) {
      state.user = {
        ...state.user,
        settings: _.merge({}, state.user.settings, action.payload),
      };
    },
  },
});

const initSocket = (authToken: string) => {
  const socket = new SlabSocket(authToken);

  if (process.env.NODE_ENV === 'development') {
    // @ts-expect-error
    window.socket = socket;
  }

  socket.onClose((event) => {
    addBreadcrumb({
      category: 'socket.close',
      message: `closed with code ${event.code}`,
      level: 'warning',
    });
  });

  socket.onError(() => {
    addBreadcrumb({
      category: 'socket.error',
      level: 'error',
    });
  });

  socket.onOpen(() => {
    addBreadcrumb({
      category: 'socket.open',
      level: 'info',
    });
  });

  socket.connect();
  return socket;
};

const clearSession = (): AppThunk => (__, getState) => {
  const { session } = getState();
  authTokenUtil.remove();

  if (session.organization.id) {
    LoggedInTeams.remove(session.organization);
  } else if (isElectron) {
    LoggedInTeams.removeAll();
  }

  purgePersisted();
  purgeUIPersistedState();
  purgeApolloCache();
  if (session.socket) {
    session.socket.disconnect();
  }
  updateApolloLink({ authToken: null, socket: null });

  analytics.ready(() => {
    analytics.reset();
    window.Intercom?.('shutdown');
  });
};

const INSTALL_VENDORS_ORGANIZATION_FRAGMENT = gql(`
  fragment installVendors_organization on Organization {
    id
    host
    planTier
    insertedAt
    subscriptionId
    ...initAnalytics_organization
  }
`);

const INSTALL_VENDORS_USER_FRAGMENT = gql(`
  fragment installVendors_user on User {
    id
    name
    email
    title
    type
    insertedAt
    ...initAnalytics_user
  }
`);

function installVendors({
  organization,
  user,
  intercomHash,
}: {
  organization: DocumentType<typeof INSTALL_VENDORS_ORGANIZATION_FRAGMENT>;
  user: DocumentType<typeof INSTALL_VENDORS_USER_FRAGMENT>;
  intercomHash: string;
}) {
  try {
    Logger.setContext(
      {
        id: user.id,
        name: user.name,
        email: user.email,
        title: user.title,
        type: _.toLower(user.type ?? ''),
        createdAt: user.insertedAt,
        sessionStartedAt: new Date().toISOString(),
      },
      {
        id: organization.id,
        host: organization.host,
        plan: organization.planTier ?? undefined,
        createdAt: organization.insertedAt,
        subscriptionId: organization.subscriptionId ?? undefined,
      },
    );
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
  initAnalytics(organization, user, intercomHash);
}

// eslint-disable-next-line slab-graphql/no-unused-fields
const START_SESSION_SESSION_FRAGMENT = gql(`
  fragment startSession_session on Session {
    authToken
    intercomHash
    organization {
      id
      insertedAt
      ...installVendors_organization
      ...setCurrentOrganization_organization
    }
    user {
      id
      email
      name
      title
      description
      type
      insertedAt
      settings {
        ...setUserSetting_userSetting
      }
      avatar {
        thumb
        original
      }
      ...installVendors_user
    }
  }
`);

const startSession =
  ({
    authToken,
    intercomHash,
    organization,
    user,
  }: DocumentType<typeof START_SESSION_SESSION_FRAGMENT>): AppThunk =>
  (dispatch) => {
    authTokenUtil.set(authToken);
    const socket = initSocket(authToken);
    updateApolloLink({ authToken, socket });

    LoggedInTeams.add(organization);

    intercomFacade.onError(() => {
      dispatch(UIActions.pushToast(miscToasts.messagingLoadError));
    });

    installVendors({ organization, user, intercomHash });
    dispatch(sessionSlice.actions.setCurrentOrganization(organization));
    dispatch(sessionSlice.actions.setCurrentUser(user));
    dispatch(sessionSlice.actions.sessionStart({ socket }));

    if (isElectron) {
      window.desktop?.startSession?.(user.type);
    }
    return Promise.resolve();
  };

const START_VISITOR_SESSION_SESSION_FRAGMENT = gql(`
  fragment startVisitorSession_session on VisitorSession {
    authToken
    user {
      id
      name
    }
  }
`);

const startVisitorSession =
  ({
    authToken,
    user,
  }: DocumentType<typeof START_VISITOR_SESSION_SESSION_FRAGMENT>): AppThunk =>
  (dispatch) => {
    visitorAuthTokenUtil.set(authToken);
    const socket = initSocket(authToken);
    updateApolloLink({ authToken, socket });

    dispatch(
      sessionSlice.actions.setCurrentUser({
        ...initialUser,
        id: user.id,
        name: user.name,
        type: UserType.Visitor,
      }),
    );

    dispatch(sessionSlice.actions.visitorSessionStart({ socket }));
  };

export const SessionActions = {
  clearSession,
  startSession,
  startVisitorSession,
  ...sessionSlice.actions,
};

export default sessionSlice.reducer;
